import {type Epic, ofType} from 'redux-observable';
import {Observable, EMPTY} from 'rxjs';
import moment from 'moment-timezone';
import {catchError, debounceTime, takeUntil, switchMap, map, skip, mergeMap} from 'rxjs/operators';

import {combineEpics} from 'web-app/util/redux-observable';
import {exhaustiveCheck} from 'web-app/util/typescript';
import * as BillPayerActions from 'web-app/react/entities/bill-payers/actions';
import * as RevenueAPI from 'web-app/api/revenue-api';
import {type InferAction} from 'web-app/util/redux/typed-action-helpers';
import {type RootState} from 'web-app/react/redux/main-reducer';

import {RefreshRevenueProgressStatus, type RevenueProgressResult} from './types';
import * as Actions from './actions';
import * as Constants from './constants';

const getFetchObservable = (
    targetId: string,
    targetType: Constants.REVENUE_TARGET_TYPES,
    from: string,
    to: string,
    refresh: boolean,
    filter: Constants.REVENUE_FILTER | null,
) => {
    switch (targetType) {
        case Constants.REVENUE_TARGET_TYPES.INSTITUTION:
            return RevenueAPI.fetchInstitutionRevenueOverview(targetId, from, to, refresh, filter);
        case Constants.REVENUE_TARGET_TYPES.ORGANIZATON:
            return RevenueAPI.fetchOrganizationRevenueOverview(targetId, from, to, refresh, filter);
        default:
            throw Error(`Unknown type "${targetType}" given to revenue report epic`);
    }
};

export const fetchRevenueReportObservable = (
    targetId: string,
    targetType: Constants.REVENUE_TARGET_TYPES,
    from: string,
    to: string,
    refresh: boolean,
    action$: any,
    revenueFilter: Constants.REVENUE_FILTER | null = null,
) => {
    return getFetchObservable(targetId, targetType, from, to, refresh, revenueFilter).map(response => ({
        response,
        actions: [
            response.billPayers ? BillPayerActions.fetchAllSuccess.action({billPayers: response.billPayers}) : null,
            Actions.fetchSuccess.action(response),
        ].filter(a => a),
    }));
};

// this stream keeps polling the current state and doesn't react
// if there are any changes but simply updates and shows the latest state on the frontend
const onlyFetchProgress = action$ =>
    action$.pipe(
        ofType(Actions.fetchProgress.type),
        switchMap(({payload}: ReturnType<typeof Actions.fetchProgress.action>) => {
            return Observable.timer(0, 5000).pipe(
                mergeMap(() => {
                    const fromMoment = moment().year(Number(payload.year)).month(payload.firstMonth).startOf('month');
                    const toMoment = fromMoment
                        .clone()
                        .add(Number(payload.numberOfMonths) - 1, 'months')
                        .endOf('month');
                    switch (payload.targetType) {
                        case Constants.REVENUE_TARGET_TYPES.ORGANIZATON:
                            return RevenueAPI.fetchOrganizationRevenueProgress(
                                payload.targetId,
                                fromMoment.format(Constants.DATE_FORMAT),
                                toMoment.format(Constants.DATE_FORMAT),
                            );
                        case Constants.REVENUE_TARGET_TYPES.INSTITUTION:
                            return RevenueAPI.fetchInstitutionRevenueProgress(
                                payload.targetId,
                                fromMoment.format(Constants.DATE_FORMAT),
                                toMoment.format(Constants.DATE_FORMAT),
                            );
                        default:
                            exhaustiveCheck(payload.targetType);
                            throw Error(`Unknown targetType "${payload.targetType}" given to revenue report epic`);
                    }
                }),
                map((res: RevenueProgressResult) => {
                    return Actions.progressUpdate.action.action({result: res, originalPayload: payload});
                }),
                takeUntil(
                    Observable.merge(
                        action$.ofType(Actions.stopPolling.type),
                        action$.ofType(Actions.refreshUpdateStopped.type),
                    ),
                ),
            );
        }),
    );

// this stream reacts to changes of state if the user is already on this page
const handleProgressChanged = action$ =>
    action$.pipe(
        ofType(Actions.progressUpdate.action.type),
        skip(1),
        mergeMap((action: ReturnType<typeof Actions.progressUpdate.action.action>) => {
            const {status} = action.payload.result;

            if (status === RefreshRevenueProgressStatus.COMPLETED) {
                const {targetId, targetType, year, filter, firstMonth, numberOfMonths} = action.payload.originalPayload;

                return [
                    Actions.refreshUpdateStopped.action(),
                    Actions.fetch.action(targetId, targetType, year, false, filter, firstMonth, numberOfMonths),
                ];
            }

            return EMPTY;
        }),
    );

const fetch: Epic<InferAction<typeof Actions['fetch']>, any, RootState, {}> = (action$: Observable<any>) =>
    action$.pipe(
        ofType(Actions.fetch.type),
        debounceTime(500),
        mergeMap(({payload}: ReturnType<typeof Actions.fetch.action>) => {
            const {
                year,
                firstMonth = Constants.DEFAULT_FIRST_MONTH,
                numberOfMonths = Constants.DEFAULT_NUMBER_OF_MONTHS,
            } = payload;
            const fromMoment = moment().year(Number(year)).month(firstMonth).startOf('month');
            const toMoment = fromMoment
                .clone()
                .add(Number(numberOfMonths) - 1, 'months')
                .endOf('month');

            return fetchRevenueReportObservable(
                payload.targetId,
                payload.targetType,
                fromMoment.format(Constants.DATE_FORMAT),
                toMoment.format(Constants.DATE_FORMAT),
                payload.refresh,
                action$,
                payload.filter,
            ).pipe(
                mergeMap(({actions}) => actions),
                catchError(e => Observable.of(Actions.fetchFailed.action(e))),
            );
        }),
    );

export default combineEpics(fetch, onlyFetchProgress, handleProgressChanged);
